import { NgModule, ErrorHandler, ModuleWithProviders, Injectable, ComponentRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list'
import { MatTooltipModule, MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip'

import { NgxMatDatetimepicker, NgxMatDatepickerInput, NgxMatTimepickerComponent } from '@imario42/datetime-picker';
import {
  NgxMatMomentModule,
  NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  NgxMatMomentDateAdapterOptions
} from '@imario42/moment-adapter';

import {
  MatMomentDateModule,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateAdapterOptions
} from '@angular/material-moment-adapter';


import { NgApexchartsModule } from 'ng-apexcharts';

import { ComponentIO, ComponentInputKey, DynamicModule } from 'ng-dynamic-component';

import {
  ActiveReportsModule,
  AR_EXPORTS,
  PdfExportService,
  XlsxExportService,
  TabularDataExportService,
  HtmlExportService
} from '@mescius/activereportsjs-angular';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader'

import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';

import { DialogComponent } from './components/dialog.component';

import {
  GridComponent,
  GridCellDisplayControlDef,
  GridCellEditControlDef,
  GridFilterControlDef,
  GridColumnDef,
  ExpandableRowDef
} from './components/grid.component';

import {
  ToolbarComponent,
  ToolbarToolDef,
} from './components/toolbar.component';

import {
  ListComponent,
  ListItemDef
} from './components/list.component';

import { DropdownSingleComponent } from './components/dropdown-single.component';
import { DropdownMultiComponent } from './components/dropdown-multi.component';
import { DateboxComponent } from './components/datebox.component';
import { DrawBoxComponent } from './components/draw-box.component';
import { NumberBoxDirective } from './components/number-box.directive';
import { TextBoxDirective } from './components/text-box.directive';
import { FormatTextPipe } from './components/formatText.pipe';
import { ConfirmationDialogComponent } from './components/confirmation-dialog.component';
import { ImageViewerDialogComponent } from './components/image-viewer-dialog.component';
import { JsonformatterDirective } from './components/jsonformatter.directive';
import { CodeValidatorDirective } from './components/code-validator.directive';

import { DatexErrorHandler } from './error/datex-error-handler';
import { ErrorComponent } from './error/error.component';
import { ErrorDialogComponent } from './error/error-dialog.component';

//#region Calendar
import { CalendarDayViewComponent } from './components/calendar/calendar-day-view.component';
import { CalendarWeekViewCurrentTimeMarkerComponent } from './components/calendar/calendar-week-view-current-time-marker.component';
import { CalendarWeekViewHourSegmentComponent } from './components/calendar/calendar-week-view-hour-segment.component';
import { CalendarDatePipe } from './components/calendar/calendar-date.pipe';
import { DateAdapter } from './components/calendar/date-adapter';
import { CalendarDateFormatter } from './components/calendar/calendar-date-formatter.provider';
import { SafeUrlPipe } from './components/embed/safe-url.pipe';
//#endregion

import { WizardComponent } from './components/wizard.component';
import { SelectorComponent } from './components/selector.component';

import { AngularSignaturePadModule } from '@almothafar/angular-signature-pad';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { ImageViewerModule } from './components/image-viewer/image-viewer.module';
import { ToastrModule } from 'ngx-toastr';
import { CommonModule } from '@angular/common';

// AG GRID
import { AgGridAngular } from '@ag-grid-community/angular';
import { AgGridComponent } from './components/ag-grid/ag-grid.component';
import { AgGridCustomCommandCellEditor } from './components/ag-grid/ag-grid-command.component';
import { AgGridCustomStatusBarPaginationComponent } from './components/ag-grid/ag-grid-status-paging.component';
import { AgGridCustomRowExpandCellRenderer } from './components/ag-grid/ag-grid-expand.component';
import { AgGridCustomCellRenderer } from './components/ag-grid/ag-grid-cell-renderer.component';
import { AgGridCustomCellEditor } from './components/ag-grid/ag-grid-cell-editor.component';
import { AgGridCustomStatusBarAddLineComponent } from './components/ag-grid/ag-grid-status-add.component';
import { AgGridRowOptionsCellRenderer } from './components/ag-grid/ag-grid-row-options.component';
import { EMPTY, Observable, isObservable } from 'rxjs';
import { isNil } from 'lodash-es';
import { AgGridFilterComponent } from './components/ag-grid/ag-grid-filter.component';
import { GridFilterDialogComponent } from './components/grid-filter-dialog.component';


const momentDateAdapterConfig: MatMomentDateAdapterOptions = {
  useUtc: true
};

const ngxMomentDateAdapterConfig: NgxMatMomentDateAdapterOptions = {
  useUtc: true
};

export const matTooltipDefaultOptions: MatTooltipDefaultOptions = {
  showDelay: 500,
  hideDelay: 250,
  touchendHideDelay: 100,
  tooltipClass: "datex-control-tooltip"
};

// we want to override the default getOutput implementation which throws
// when the content component doesn't have @Outputs that match the 
// [ndcDynamicOutputs]="outputs" we pass, essentially restoring the behaviour
// in versions <= 10.7.0, see https://github.com/gund/ng-dynamic-component/issues/526
@Injectable()
export class CustomClassicComponentIO implements ComponentIO {
  setInput<T, K extends ComponentInputKey<T>>(
    componentRef: ComponentRef<T>,
    name: K,
    value: T[K],
  ): void {
    componentRef.setInput(name, value);
  }

  getOutput<T, K extends ComponentInputKey<T>>(
    componentRef: ComponentRef<T>,
    name: K,
  ): Observable<unknown> {
    const output = componentRef.instance[name];

    if (isNil(output)) {
      return EMPTY;
    }

    if (!isObservable(output)) {
      throw new Error(`Output ${name} is not an observable!`);
    }

    return output;
  }
}

@NgModule({
  declarations: [
    DialogComponent,
    ConfirmationDialogComponent,
    ImageViewerDialogComponent,
    ErrorComponent,
    ErrorDialogComponent,
    JsonformatterDirective,
    AgGridCustomCommandCellEditor,
    AgGridCustomStatusBarAddLineComponent,
    AgGridCustomStatusBarPaginationComponent,
    AgGridCustomRowExpandCellRenderer,
    AgGridCustomCellRenderer,
    AgGridCustomCellEditor,
    AgGridRowOptionsCellRenderer,
    AgGridComponent,
    AgGridFilterComponent,
    GridComponent,
    GridCellDisplayControlDef,
    GridCellEditControlDef,
    GridFilterControlDef,
    GridColumnDef,
    GridFilterDialogComponent,
    ExpandableRowDef,
    ListItemDef,
    ListComponent,
    DropdownSingleComponent,
    DropdownMultiComponent,
    DateboxComponent,
    DrawBoxComponent,
    NumberBoxDirective,
    TextBoxDirective,
    FormatTextPipe,
    //#region Calendar
    CalendarDayViewComponent,
    CalendarWeekViewCurrentTimeMarkerComponent,
    CalendarWeekViewHourSegmentComponent,
    CalendarDatePipe,
    //#endregion
    SafeUrlPipe,
    WizardComponent,
    SelectorComponent,
    CodeValidatorDirective,
    ToolbarComponent,
    ToolbarToolDef,
  ],
  imports: [
    CommonModule,
    ImageViewerModule.forRoot({
      containerBackgroundColor: '#fff',
      allowFullscreen: true,
      btnIcons: {
        fullscreen: 'icon icon-ic_fluent_full_screen_maximize_20_regular',
        modalScreen: 'icon icon-ic_fluent_full_screen_minimize_20_regular',
        zoomOut: 'icon icon-ic_fluent_zoom_out_20_regular',
        zoomIn: 'icon icon-ic_fluent_zoom_in_20_regular',
        rotateCounterClockwise: 'icon icon-ic_fluent_arrow_rotate_counterclockwise_20_regular',
        rotateClockwise: 'icon icon-ic_fluent_arrow_rotate_clockwise_20_regular'
      },
      btnShow: {
        zoomIn: true,
        zoomOut: true,
        rotateClockwise: true,
        rotateCounterClockwise: true
      }
    }),
    CodemirrorModule,
    AngularSignaturePadModule,
    FormsModule,
    ReactiveFormsModule,
    LoadingBarHttpClientModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    MatProgressBarModule,
    MatCardModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatPseudoCheckboxModule,
    MatListModule,
    NgApexchartsModule,
    NgxMatDatetimepicker,
    NgxMatDatepickerInput,
    NgxMatTimepickerComponent,
    NgxMatMomentModule,
    DynamicModule,
    ActiveReportsModule,
    TranslateModule.forRoot(),
    ToastrModule.forRoot(),
    AgGridAngular,
  ],
  exports: [
    DialogComponent,
    ToolbarComponent,
    ToolbarToolDef,
    ConfirmationDialogComponent,
    ImageViewerDialogComponent,
    ErrorComponent,
    ErrorDialogComponent,
    JsonformatterDirective,
    AgGridComponent,
    GridComponent,
    GridCellDisplayControlDef,
    GridCellEditControlDef,
    GridFilterControlDef,
    GridColumnDef,
    ExpandableRowDef,
    ListItemDef,
    ListComponent,
    DropdownSingleComponent,
    DropdownMultiComponent,
    DateboxComponent,
    DrawBoxComponent,
    NumberBoxDirective,
    TextBoxDirective,
    FormatTextPipe,
    //#region Calendar
    CalendarDayViewComponent,
    CalendarWeekViewCurrentTimeMarkerComponent,
    CalendarWeekViewHourSegmentComponent,
    CalendarDatePipe,
    //#endregion
    SafeUrlPipe,
    WizardComponent,
    SelectorComponent,
    CodeValidatorDirective,
    CommonModule,
    ImageViewerModule,
    CodemirrorModule,
    AngularSignaturePadModule,
    FormsModule,
    ReactiveFormsModule,
    LoadingBarHttpClientModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    MatProgressBarModule,
    MatCardModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatPseudoCheckboxModule,
    MatListModule,
    NgApexchartsModule,
    NgxMatDatetimepicker,
    NgxMatDatepickerInput,
    NgxMatTimepickerComponent,
    NgxMatMomentModule,
    DynamicModule,
    ActiveReportsModule,
    TranslateModule,
    AgGridAngular
  ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        {
          provide: ErrorHandler,
          useClass: DatexErrorHandler
        },
        {
          provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
          useValue: momentDateAdapterConfig
        },
        {
          provide: NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS,
          useValue: ngxMomentDateAdapterConfig
        },
        {
          provide: AR_EXPORTS,
          useClass: PdfExportService,
          multi: true
        },
        {
          provide: AR_EXPORTS,
          useClass: XlsxExportService,
          multi: true
        },
        {
          provide: AR_EXPORTS,
          useClass: TabularDataExportService,
          multi: true
        },
        {
          provide: AR_EXPORTS,
          useClass: HtmlExportService,
          multi: true
        },
        //#region Calendar
        DateAdapter,
        CalendarDateFormatter,
        {
          provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
          useValue: matTooltipDefaultOptions
        },
        {
          provide: ComponentIO,
          useClass: CustomClassicComponentIO
        }
      ]
    }
  }
}